import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useFeatureFlagsApi } from '@/hooks/api/useFeatureFlagsApi';
import { useOrganizations } from '@/hooks/api/useOrganizations';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';

/**
 * KIR-4443: whether package Block / Unblock controls may be shown for this
 * scope — on the Packages page and on a package alert alike.
 *
 * - **Flag.** The packages policies exist only behind `malicious_packages`.
 *   For an organization it is read from the backend with the org id, the same
 *   evaluation the backend registry makes, so an org-targeted rollout shows
 *   the controls exactly where the scanner runs (KIR-4096's fix for Edit
 *   Policy). The personal scope has no org to evaluate and uses the SDK.
 * - **Scope.** An org-scanned install is judged by the org's list, not the
 *   user's, so the personal scope offers controls only to users with no
 *   organization. Otherwise a member's personal Block would be a rule no org
 *   scan consults.
 *
 * Pass `enabled: false` where no package is on screen (a non-package alert)
 * to skip the org flag request; the answer is then `available: false`.
 */
export function usePackageListsAvailable(
  organizationId: string | undefined,
  enabled: boolean = true
): {
  available: boolean;
  isLoading: boolean;
} {
  const isOrg = organizationId != null;
  const serverFlags = useFeatureFlagsApi(
    [flagsmithFeatureFlags.MALICIOUS_PACKAGES],
    isOrg && enabled,
    organizationId
  );
  const clientFlags = useFeatureFlags(
    isOrg ? [] : [flagsmithFeatureFlags.MALICIOUS_PACKAGES]
  );
  const organizations = useOrganizations();

  if (!enabled) return { available: false, isLoading: false };
  if (isOrg) {
    return {
      available:
        serverFlags.data?.[flagsmithFeatureFlags.MALICIOUS_PACKAGES] ?? false,
      isLoading: serverFlags.isLoading,
    };
  }
  const flagOn =
    clientFlags.flags[flagsmithFeatureFlags.MALICIOUS_PACKAGES] ?? false;
  return {
    available:
      flagOn && organizations.data != null && organizations.data.length === 0,
    isLoading: clientFlags.isLoading || organizations.isLoading,
  };
}
